import { useMutation, useQueryClient } from '@tanstack/react-query';

import {
  BuildFlightCampaignBody,
  buildFlightCampaign,
  publishFlightCampaign,
  unpublishFlightCampaign,
} from '~/services/FlightsService';

import { corridorOpportunityQueryKeys } from './useCorridorOpportunities';

function useRefreshCampaignBuild() {
  const queryClient = useQueryClient();
  return () =>
    Promise.all([
      ...['flightCampaigns', 'flightCampaign', 'flightCampaignDeals', 'flightDeal'].map((key) =>
        queryClient.invalidateQueries({ queryKey: [key] }),
      ),
      queryClient.invalidateQueries({ queryKey: corridorOpportunityQueryKeys.all }),
    ]);
}

export function useValidateFlightCampaignBuild() {
  return useMutation({
    mutationFn: (body: BuildFlightCampaignBody) => buildFlightCampaign({ ...body, validateOnly: true }),
  });
}

export function useBuildFlightCampaign() {
  const refresh = useRefreshCampaignBuild();
  return useMutation({
    mutationFn: (body: BuildFlightCampaignBody) => buildFlightCampaign(body),
    onSuccess: (response) => (response.status === 200 ? refresh() : undefined),
    // A lost response can follow a committed write. Reload its opportunity before allowing another build.
    onError: refresh,
  });
}

export function usePublishFlightCampaign() {
  const refresh = useRefreshCampaignBuild();
  return useMutation({
    mutationFn: (id: string) => publishFlightCampaign(id),
    onSuccess: refresh,
    onError: refresh,
  });
}

export function useUnpublishFlightCampaign() {
  const refresh = useRefreshCampaignBuild();
  return useMutation({ mutationFn: (id: string) => unpublishFlightCampaign(id), onSuccess: refresh, onError: refresh });
}
